import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, Copy, Loader2 } from 'lucide-react'
import { MarketingShell } from './MarketingShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { describeApiError } from '@/lib/apiError'
import type { ReviewSettings } from '@/types'

/** Matches receiptCopyMaxLen in backend/internal/handlers/marketing_reviews.go.
 *  The headline prints on a `white-space: nowrap` line at 80mm — longer copy
 *  runs off the edge of the roll rather than wrapping. */
const RECEIPT_COPY_MAX = 32

/**
 * Review settings — the owner's one-time setup.
 *
 * The worklist lives at /admin/marketing/recovery so a manager can work it
 * without also getting the Google link, the gating toggle and the alert
 * routing, all of which are the owner's call.
 */
export function AdminReviews() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [draft, setDraft] = useState<ReviewSettings | null>(null)

  const settingsQuery = useQuery({
    queryKey: ['marketing', 'review-settings'],
    queryFn: () => apiClient.getReviewSettings(),
    staleTime: 60_000,
  })
  const baseQuery = useQuery({
    queryKey: ['marketing', 'public-base'],
    queryFn: () => apiClient.listMarketingQR(),
    staleTime: 300_000,
  })

  const current = draft ?? settingsQuery.data?.data ?? null
  const publicBase = (baseQuery.data?.data?.redirect_base ?? '').replace(/\/+$/, '')
  const posterUrl = publicBase ? `${publicBase}/q/review` : ''

  const save = useMutation({
    mutationFn: (body: ReviewSettings) => apiClient.updateReviewSettings(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'review-settings'] })
      setDraft(null)
      toast({ title: 'Review settings saved' })
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not save',
        description: describeApiError(err).message,
        variant: 'destructive',
      })
    },
  })

  const set = (patch: Partial<ReviewSettings>) => {
    if (current) setDraft({ ...current, ...patch })
  }

  return (
    <MarketingShell
      title="Reviews"
      description="Happy guests go to Google. Unhappy guests come to you first — private feedback you can act on before it becomes a public one-star."
    >
      <div className="space-y-5">
        <Link
          to="/admin/marketing/recovery"
          className="flex items-center gap-1.5 rounded-lg border border-border/80 bg-background p-3 text-sm font-medium hover:bg-muted/50"
        >
          Open the guest recovery worklist
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Link>

        {settingsQuery.isError && (
          <div className="rounded-lg border border-destructive/50 p-4 text-sm text-destructive">
            Couldn’t load review settings: {describeApiError(settingsQuery.error).message}
          </div>
        )}

        {current && (
          <div className="rounded-lg border border-border/80 bg-background p-4 shadow-sm">
            <h3 className="font-semibold">Settings</h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="rv-url">Google review link</Label>
                <Input
                  id="rv-url"
                  value={current.google_review_url}
                  onChange={(e) => set({ google_review_url: e.target.value })}
                  placeholder="https://g.page/r/…/review"
                />
                <p className="text-xs text-muted-foreground">
                  Google Business Profile → “Ask for reviews” gives you this
                  link. Must start with https://. Without it, every rating stays
                  private.
                </p>
              </div>

              <div className="space-y-1">
                <Label>Send to Google from</Label>
                <Select
                  value={String(current.threshold)}
                  onValueChange={(v) => set({ threshold: Number(v) })}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[3, 4, 5].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n} stars and up
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="rv-owner">Low-rating WhatsApp alert to</Label>
                <Input
                  id="rv-owner"
                  value={current.owner_alert_phone}
                  onChange={(e) => set({ owner_alert_phone: e.target.value })}
                  placeholder="0300 1234567"
                />
              </div>

              <div className="space-y-1">
                <Label>Alert me at or below</Label>
                <Select
                  value={String(current.alert_threshold)}
                  onValueChange={(v) => set({ alert_threshold: Number(v) })}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n} star{n === 1 ? '' : 's'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  If you treat 3 stars as a failure, set it to 3 — otherwise
                  those complaints never wake anyone.
                </p>
              </div>

              <label className="flex items-start gap-2 text-sm sm:col-span-2">
                <Switch
                  checked={current.gated}
                  onCheckedChange={(v) => set({ gated: v })}
                />
                <span>
                  Rating gate on
                  <span className="block text-xs text-muted-foreground">
                    Off = every rating redirects to Google regardless of stars
                    (the policy-compliant mode — Google prohibits review
                    gating, and can remove reviews if gating is detected).
                    Ratings are still stored either way.
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-2 text-sm sm:col-span-2">
                <Switch
                  checked={current.receipt_qr_enabled}
                  onCheckedChange={(v) => set({ receipt_qr_enabled: v })}
                />
                <span>
                  Print a feedback QR on receipts
                  <span className="block text-xs text-muted-foreground">
                    The only way to hear from a walk-in who paid cash and left
                    no number. The code carries the order, so their rating
                    arrives with the bill, the server and the time attached.
                  </span>
                </span>
              </label>

              {current.receipt_qr_enabled && (
                <label className="ml-6 flex items-start gap-2 text-sm sm:col-span-2">
                  <Switch
                    checked={current.receipt_qr_with_loyalty}
                    onCheckedChange={(v) => set({ receipt_qr_with_loyalty: v })}
                  />
                  <span>
                    Print it even when the loyalty QR is on the slip
                    <span className="block text-xs text-muted-foreground">
                      Off = loyalty wins and the feedback QR is skipped on those
                      receipts (shorter slip, one clear ask). On = both codes
                      print — the receipt gets longer, and guests handed two QRs
                      tend to scan fewer of either, but it's your paper and your
                      call.
                    </span>
                  </span>
                </label>
              )}

              <div className="space-y-1">
                <Label htmlFor="rv-cooldown">Don’t re-ask the same guest for</Label>
                <Input
                  id="rv-cooldown"
                  type="number"
                  min={0}
                  max={365}
                  value={current.ask_cooldown_days}
                  onChange={(e) => set({ ask_cooldown_days: Number(e.target.value) })}
                />
                <p className="text-xs text-muted-foreground">
                  Days, 0–365. Applies to the receipt QR and the WhatsApp ask
                  alike. Only enforceable for guests we can recognise; 0 asks
                  every visit.
                </p>
              </div>

              <div className="space-y-1">
                <Label htmlFor="rv-headline">Receipt headline</Label>
                <Input
                  id="rv-headline"
                  maxLength={RECEIPT_COPY_MAX}
                  value={current.receipt_headline}
                  onChange={(e) => set({ receipt_headline: e.target.value })}
                  placeholder="How did we do?"
                />
                <p className="text-xs text-muted-foreground">
                  Up to {RECEIPT_COPY_MAX} characters so it fits one line on the roll.
                </p>
              </div>

              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="rv-subline">Receipt sub-line</Label>
                <Input
                  id="rv-subline"
                  maxLength={RECEIPT_COPY_MAX}
                  value={current.receipt_subline}
                  onChange={(e) => set({ receipt_subline: e.target.value })}
                  placeholder="Tell us in ten seconds."
                />
              </div>

              {posterUrl && (
                <div className="space-y-1 sm:col-span-2">
                  <Label>Poster / table-tent link</Label>
                  <div className="flex gap-2">
                    <Input readOnly value={posterUrl} className="font-mono text-sm" />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0 gap-1.5"
                      onClick={() => {
                        void navigator.clipboard?.writeText(posterUrl)
                        toast({ title: 'Link copied' })
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" aria-hidden />
                      Copy
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Point a QR code at this for a table tent or counter placard.
                    Poster scans are anonymous — no order, no guest — so they
                    can’t be recovered with a coupon. The receipt QR can.
                  </p>
                </div>
              )}
            </div>

            {draft && (
              <div className="mt-3 flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setDraft(null)} disabled={save.isPending}>
                  Cancel
                </Button>
                <Button size="sm" onClick={() => save.mutate(draft)} disabled={save.isPending}>
                  {save.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                  Save
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </MarketingShell>
  )
}
